// ----------------------------------------------------------------------------
// Sidebar
// ----------------------------------------------------------------------------
// Fixed, full-height navigation column. Owns no data fetching — the active
// view is controlled by the parent <App /> via the `currentView` / `onSelect`
// props so the rest of the dashboard stays a pure function of state.
// ----------------------------------------------------------------------------

import React from "react";
import {
  Gavel,
  Bookmark,
  BellRing,
  Code2,
  LifeBuoy,
  Compass,
  Sliders,
  LogOut,
} from "lucide-react";
import { NAV, AUTH } from "../constants/testIds";
import { useAuth } from "../context/AuthContext";

const NAV_ITEMS = [
  { key: "active", label: "Active Bids", icon: Gavel },
  { key: "saved", label: "Saved Contracts", icon: Bookmark },
  { key: "alerts", label: "Alert Settings", icon: BellRing },
  { key: "api", label: "Developer API", icon: Code2 },
];

// Admin items are visually separated from the workspace nav by a divider.
const ADMIN_ITEMS = [
  { key: "wizard", label: "Owner Setup Wizard", icon: Sliders },
];

export const Sidebar = ({ currentView, onSelect }) => {
  const { signOut, currentUser } = useAuth();
  return (
    <aside
      data-testid={NAV.sidebar}
      className="hidden md:flex w-64 flex-shrink-0 flex-col bg-[#141d2b] text-slate-100 border-r border-slate-800 sticky top-0 h-screen"
    >
      {/* Brand block */}
      <div
        data-testid={NAV.logo}
        className="px-6 pt-7 pb-6 border-b border-slate-800/80"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-[#1e293b] border border-slate-700/70 flex items-center justify-center">
            <Compass size={20} strokeWidth={1.6} className="text-sky-400" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-[15px] font-semibold tracking-tight text-white">
              Space Coast
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              Procurement
            </span>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 py-5 space-y-1">
        <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Workspace
        </div>
        {NAV_ITEMS.map(({ key, label, icon: Icon }) => {
          const isActive = currentView === key;
          return (
            <button
              key={key}
              data-testid={NAV.item(key)}
              onClick={() => onSelect(key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 group relative ${
                isActive
                  ? "bg-[#1e293b] text-white"
                  : "text-slate-300 hover:bg-[#1a2536] hover:text-white"
              }`}
            >
              {isActive && (
                <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-sky-400" />
              )}
              <Icon
                size={17}
                strokeWidth={1.6}
                className={`transition-transform duration-150 ${
                  isActive ? "text-sky-400" : "text-slate-400 group-hover:scale-110"
                }`}
              />
              <span className="font-medium">{label}</span>
            </button>
          );
        })}

        {/* Admin section — divider then logout + wizard entry. */}
        <div className="pt-4">
          <div className="mx-3 border-t border-slate-800/80" />
          <div className="px-3 pt-3 pb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Admin
          </div>

          {/* Log out — sits directly above the Owner Setup Wizard per spec. */}
          <button
            type="button"
            data-testid={AUTH.logout}
            onClick={signOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 group text-slate-300 hover:bg-[#1a2536] hover:text-white"
          >
            <LogOut
              size={17}
              strokeWidth={1.6}
              className="text-slate-400 group-hover:scale-110 transition-transform duration-150"
            />
            <span className="font-medium">Log Out</span>
          </button>

          {ADMIN_ITEMS.map(({ key, label, icon: Icon }) => {
            const isActive = currentView === key;
            return (
              <button
                key={key}
                data-testid={NAV.item(key)}
                onClick={() => onSelect(key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 group relative ${
                  isActive
                    ? "bg-[#1e293b] text-white"
                    : "text-slate-300 hover:bg-[#1a2536] hover:text-white"
                }`}
              >
                {isActive && (
                  <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-sky-400" />
                )}
                <Icon
                  size={17}
                  strokeWidth={1.6}
                  className={`transition-transform duration-150 ${
                    isActive ? "text-sky-400" : "text-slate-400 group-hover:scale-110"
                  }`}
                />
                <span className="font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Footer / support card */}
      <div className="p-4 border-t border-slate-800/80">
        {currentUser && (
          <div className="mb-3 px-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Signed in as
            </p>
            <p className="text-xs text-slate-200 font-medium truncate" title={currentUser.email}>
              {currentUser.email}
            </p>
            {currentUser.company && (
              <p className="text-[11px] text-slate-400 truncate" title={currentUser.company}>
                {currentUser.company}
              </p>
            )}
          </div>
        )}
        <div className="rounded-lg bg-[#1a2536] border border-slate-700/60 p-3.5">
          <div className="flex items-center gap-2 mb-1.5">
            <LifeBuoy size={14} strokeWidth={1.6} className="text-sky-400" />
            <span className="text-xs font-semibold text-slate-200">
              Vendor Support
            </span>
          </div>
          <p className="text-[11px] leading-relaxed text-slate-400">
            Brevard, Palm Bay & W. Melbourne — live monitoring active.
          </p>
        </div>
        <p className="px-1 mt-4 text-[10px] text-slate-500">
          v1.0 · investor preview
        </p>
      </div>
    </aside>
  );
};
